import { useQuery, useQueryClient } from '@tanstack/react-query';
import NewConversationService from '@apis/NewConversationService';
import { ConversationHistoryItem } from '@stores/chatbot/types';

// Query key factory for chat history
export const chatHistoryQueryKeys = {
  all: ['chatHistory'] as const,
  list: () => ['chatHistory', 'list'] as const,
};

/**
 * Hook for fetching and caching chat conversation history
 * Uses React Query for client-side caching and automatic refetching
 */
export const useChatHistory = () => {
  const queryClient = useQueryClient();

  const {
    data: conversationHistory = [],
    isLoading,
    refetch,
    error,
  } = useQuery<ConversationHistoryItem[]>({
    queryKey: chatHistoryQueryKeys.list(),
    queryFn: async () => {
      const conversations = await NewConversationService.getAll();
      // Map NewConversationListItem to ConversationHistoryItem
      // Backend already returns results sorted by last_activity_at desc
      const historyItems: ConversationHistoryItem[] = conversations.map(conv => ({
        conversation_id: conv.conversation_id,
        title: conv.title,
        timestamp: conv.timestamp,
        last_activity_at: conv.last_activity_at,
        started_by_user_name: conv.started_by_user_name,
      }));
      return historyItems;
    },
    staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  /**
   * Manually invalidate and refetch the chat history
   */
  const invalidateHistory = async () => {
    await queryClient.invalidateQueries({ queryKey: chatHistoryQueryKeys.list() });
  };

  /**
   * Remove a conversation from the cache after deletion
   */
  const removeConversationFromCache = (conversationId: string) => {
    queryClient.setQueryData<ConversationHistoryItem[]>(
      chatHistoryQueryKeys.list(),
      (oldHistory) => {
        return oldHistory?.filter(conv => conv.conversation_id !== conversationId) || [];
      }
    );
  };

  return {
    conversationHistory,
    isLoading,
    refetch,
    invalidateHistory,
    removeConversationFromCache,
    error,
  };
};

